import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchDataFromApi } from "../../utils/api";

export const fetchOrders = createAsyncThunk(
  "orders/fetchOrders",
  async (
    {
      userId,
      page = 1,
      limit = 10,
      statuses = [],
      years = [],
      last30Days = false,
      searchTerm = "",
    },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("userId", userId);
      queryParams.append("page", page);
      queryParams.append("limit", limit);
      if (statuses.length > 0) {
        statuses.forEach((status) => queryParams.append("statuses", status));
      }
      if (years.length > 0) {
        years.forEach((year) => queryParams.append("years", year));
      }
      if (last30Days) {
        queryParams.append("last30Days", true);
      }
      if (searchTerm) {
        queryParams.append("search", searchTerm);
      }

      const response = await fetchDataFromApi(
        `/api/orders?${queryParams.toString()}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.data?.message || error.message || "Failed to fetch orders"
      );
    }
  }
);

export const fetchRefundsAsync = createAsyncThunk(
  "orders/fetchRefunds",
  async ({ page = 1, limit = 10, status = "" }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("limit", limit);
      if (status) {
        queryParams.append("status", status);
      }
      const response = await fetchDataFromApi(
        `/api/refunds?${queryParams.toString()}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.data?.message || error.message || "Failed to fetch refunds"
      );
    }
  }
);

export const cancelOrderAsync = createAsyncThunk(
  "orders/cancelOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await fetchDataFromApi(`/api/orders/cancel/${orderId}`, {
        method: "PUT",
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error.data?.message || error.message || "Failed to cancel order."
      );
    }
  }
);

const initialState = {
  orders: [],
  ordersPagination: {
    currentPage: 1,
    totalPages: 0,
    totalOrders: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10,
  },
  refunds: [],
  refundsPagination: {
    currentPage: 1,
    totalPages: 0,
    totalRefunds: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10,
  },
  selectedOrder: null,
  loading: false,
  error: null,
};

const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setSelectedOrder: (state, action) => {
      state.selectedOrder = action.payload.data;
    },
    updateOrderStatus: (state, action) => {
      const { orderId, status } = action.payload;
      const order = state.orders.find((o) => o._id === orderId);
      if (order) {
        order.orderStatus = status;
      }
    },
    addOrder: (state, action) => {
      state.orders.push(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.orders = action.payload.orders;
        state.ordersPagination = action.payload.pagination;
        state.loading = false;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.orders = [];
        state.ordersPagination = {
          currentPage: 1,
          totalPages: 0,
          totalOrders: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: 10,
        };
      })
      .addCase(fetchRefundsAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchRefundsAsync.fulfilled, (state, action) => {
        state.refunds = action.payload.refunds;
        state.refundsPagination = action.payload.pagination;
      })
      .addCase(fetchRefundsAsync.rejected, (state, action) => {
        state.error = action.payload;
        state.refunds = [];
        state.refundsPagination = {
          currentPage: 1,
          totalPages: 0,
          totalRefunds: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: 10,
        };
      })
      .addCase(cancelOrderAsync.pending, (state) => {
        state.error = null;
      })
      .addCase(cancelOrderAsync.fulfilled, (state, action) => {
        const cancelledOrderData = action.payload.data.order;
        const cancelledRefundData = action.payload.data.refund;

        const index = state.orders.findIndex(
          (order) => order._id === cancelledOrderData._id
        );
        if (index !== -1) {
          state.orders[index].orderStatus = cancelledOrderData.orderStatus;
          state.orders[index].refundDetails = cancelledRefundData;
        }
      })
      .addCase(cancelOrderAsync.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setSelectedOrder, updateOrderStatus, addOrder } =
  orderSlice.actions;
export default orderSlice.reducer;
